import {Room, RoomOffer, RoomPrice} from '@findhotel/sapi'

import {getCancellationDeadline} from '@vio/core/src/offer/business/cancellationPenalties'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'

import {isOfferEligibleForDealFreeze} from '../OfferEligibility'

export const findRoomByOfferId = (rooms: Room[], offerId?: string) =>
  rooms.find(room => room.offers.find(offer => offer.id === offerId))

export const findExactMatchedOffer = (
  rooms?: Room[],
  offerId?: string | null
): RoomOffer | undefined => {
  if (!rooms) return undefined

  return rooms.flatMap(room => room.offers).find(({id}) => id === offerId)
}

// SAPI offer ids are per-search, so after sign-in the exact-id match misses.
// Matches by stable characteristics instead; only equal-or-lower price qualifies.
export const findFallbackMatchedOffer = (
  rooms: Room[],
  stashedOffer?: RoomOffer,
  stashedRoomId?: string
): RoomOffer | undefined => {
  if (!stashedOffer) return undefined

  const stashedPrice = getDisplayPrice(stashedOffer.prices ?? [])
  if (!stashedPrice) return undefined

  const candidateRooms = stashedRoomId
    ? rooms.filter(room => room.id === stashedRoomId)
    : rooms

  const qualifiers = candidateRooms
    .flatMap(room => room.offers)
    .filter(offer => isOfferEligibleForDealFreeze(offer.tags))
    .map(offer => ({
      offer,
      difference: calculatePriceMismatchDifference(
        stashedPrice,
        getDisplayPrice(offer.prices ?? []) ?? null
      )
    }))
    .filter(
      (candidate): candidate is {offer: RoomOffer; difference: string} =>
        candidate.difference !== null && Number(candidate.difference) >= 0
    )

  if (!qualifiers.length) return undefined

  return qualifiers.reduce((closest, candidate) =>
    Number(candidate.difference) < Number(closest.difference)
      ? candidate
      : closest
  ).offer
}

export const calculatePriceMismatchDifference = (
  firstPrice: OfferPrice | RoomPrice | null,
  secondPrice: OfferPrice | RoomPrice | null
) => {
  if (!firstPrice || !secondPrice) return null

  const firstTotal = getTotalPrice(firstPrice)
  const secondTotal = getTotalPrice(secondPrice)
  if (!firstTotal || !secondTotal) return null

  const mismatch = firstTotal - secondTotal
  return mismatch.toFixed(2)
}

// Board (meal-plan) comparison for the resume. Accepts both AP RoomOffer
// (`mealPlan`/`offerTitleKey`) and SRP Offer (`package.mealPlan`) shapes.
type BoardBearingOffer = {
  offerTitleKey?: string | null
  mealPlan?: {meal?: string | null}[] | null
  package?: {mealPlan?: {meal?: string | null}[] | null} | null
}

// offerTitleKey is `${board}_${cancellation}`; no board is a prefix of another.
const OFFER_TITLE_KEY_BOARDS = [
  'room_only',
  'breakfast',
  'half_board',
  'full_board',
  'all_inclusive'
]

const normalizeBoardToken = (value: string): string =>
  value.toLowerCase().replace(/[\s_-]/g, '')

const getBoardFromOfferTitleKey = (
  offerTitleKey?: string | null
): string | undefined => {
  if (!offerTitleKey) return undefined
  const board = OFFER_TITLE_KEY_BOARDS.find(candidate =>
    offerTitleKey.startsWith(`${candidate}_`)
  )
  return board ? normalizeBoardToken(board) : undefined
}

// Single confident board token, or undefined when signals conflict or are absent
// (the caller treats undefined as "not equivalent").
export const getCanonicalBoard = (
  offer?: BoardBearingOffer | null
): string | undefined => {
  if (!offer) return undefined

  const evidence = new Set<string>()

  const titleKeyBoard = getBoardFromOfferTitleKey(offer.offerTitleKey)
  if (titleKeyBoard) evidence.add(titleKeyBoard)

  const mealPlan = offer.mealPlan ?? offer.package?.mealPlan ?? []
  mealPlan.forEach(entry => {
    if (entry?.meal) evidence.add(normalizeBoardToken(entry.meal))
  })

  return evidence.size === 1 ? [...evidence][0] : undefined
}

// True only when both offers resolve to the same confident canonical board.
export const isFreezeBoardEquivalent = (
  stashed?: BoardBearingOffer | null,
  matched?: BoardBearingOffer | null
): boolean => {
  const stashedBoard = getCanonicalBoard(stashed)
  return (
    stashedBoard !== undefined && stashedBoard === getCanonicalBoard(matched)
  )
}

export type ResumeMatchType = 'exact' | 'fallback'

export interface ResumeOffer {
  total?: number
  currency?: string
  /** Free cancellation end. */
  cancellation?: string
  provider?: string
  rateId?: string
  rateType?: string
}

export interface ResumeMatch {
  resumeMatchType: ResumeMatchType
  /** Set only when the two offers differ. */
  stashedOffer?: ResumeOffer
  resumedOffer?: ResumeOffer
}

const toResumeOffer = (offer?: RoomOffer): ResumeOffer => {
  const price = getDisplayPrice(offer?.prices ?? [])

  return {
    total: price ? getTotalPrice(price) : undefined,
    currency: price?.currencyCode,
    cancellation: getCancellationDeadline(offer?.cancellationPenalties ?? []),
    provider: offer?.providerCode,
    rateId: offer?.providerRateId,
    rateType: offer?.providerRateType
  }
}

/**
 * Compares the offer a post-auth resume landed on with the stashed one.
 * @param resumeMatchType - Which lookup produced the matched offer
 * @param stashed - The offer stashed when the guest clicked freeze
 * @param matched - The offer the resume is about to freeze
 * @returns The match path, plus both offers when they differ
 */
export const describeResumeMatch = (
  resumeMatchType: ResumeMatchType,
  stashed?: RoomOffer,
  matched?: RoomOffer
): ResumeMatch => {
  const stashedOffer = toResumeOffer(stashed)
  const resumedOffer = toResumeOffer(matched)

  const mismatched =
    Boolean(stashed && matched) &&
    (stashedOffer.total !== resumedOffer.total ||
      stashedOffer.currency !== resumedOffer.currency ||
      stashedOffer.cancellation !== resumedOffer.cancellation ||
      stashedOffer.provider !== resumedOffer.provider ||
      stashedOffer.rateId !== resumedOffer.rateId ||
      stashedOffer.rateType !== resumedOffer.rateType)

  return {
    resumeMatchType,
    ...(mismatched && {stashedOffer, resumedOffer})
  }
}
